import { useCallback, useRef, useState } from 'react';

/**
 * Запись аудио с микрофона для отправки в Speech-to-Text (MediaRecorder).
 */
export function useVoiceRecording() {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (typeof MediaRecorder === 'undefined') {
      setError('Запись голоса не поддерживается в этом браузере');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      let mime = '';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mime = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mime = 'audio/webm';
      }

      const mr = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      recorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mr.start(120);
      setIsRecording(true);
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : 'Не удалось получить доступ к микрофону';
      setError(
        msg.includes('NotAllowed') || msg.includes('Permission')
          ? 'Разрешите доступ к микрофону в настройках браузера'
          : msg,
      );
      stopStream();
    }
  }, [stopStream]);

  const stop = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const mr = recorderRef.current;
      if (!mr || mr.state === 'inactive') {
        setIsRecording(false);
        resolve(null);
        return;
      }
      mr.onstop = () => {
        const mimeType = mr.mimeType || 'audio/webm';
        stopStream();
        recorderRef.current = null;
        setIsRecording(false);
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        resolve(blob.size > 0 ? blob : null);
      };
      mr.stop();
    });
  }, [stopStream]);

  const clearError = useCallback(() => setError(null), []);

  return { isRecording, error, start, stop, clearError };
}
